import { observer } from "mobx-react";
import { Box } from "./Box";
import { BoxProps } from "./boxHelpers";
import { DropdownMenu, DropdownMenuItemType } from "./Dropdown";
import { IconName } from "./iconMapping";
import { ToggleButton } from "./Button";
import { TMessage } from "@/i18n/tt";
import { DCEvent } from "@/events/eventsDomain";
import { IconProps } from "@/components-v2/Icon.tsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { debounce } from "lodash";

export type QuickFilter = {
  id: string;
  label: string | TMessage;
  icon?: IconName | IconProps;
  isActive?: boolean;
  onSelect: (() => void) | DCEvent[];
  tooltip?: string | TMessage;
};

export type QuickFilterBarProps = {
  filters: QuickFilter[];
  isExpanded?: boolean;
  onToggle?: (() => void) | DCEvent[];
  collapseFiltersLabel?: string | TMessage;
  expandFiltersLabel?: string | TMessage;
  tooltipMessage?: string | TMessage;
  wrap?: boolean;
  extraAction?: {
    label: string | TMessage;
    icon?: IconName | IconProps;
    onSelect: (() => void) | DCEvent[];
    tooltip?: string | TMessage;
  };
} & BoxProps;

export type RowMeasurements = {
  available: number;
  extents: number[];
  reserved: number;
};

const countFittingFilters = ({ available, extents, reserved }: RowMeasurements): number => {
  const limit = available - reserved;
  return extents.filter((extent) => extent <= limit).length;
};

const splitQuickFilters = (
  filters: QuickFilter[],
  visibleCount: number,
): { visible: QuickFilter[]; hidden: QuickFilter[] } => {
  if (filters.length <= visibleCount) {
    return { visible: filters, hidden: [] };
  }
  const visible = filters.slice(0, visibleCount);
  const hidden = filters.slice(visibleCount);
  const activeIndex = visibleCount < 1 ? -1 : hidden.findIndex((f) => f.isActive);
  if (activeIndex < 0) {
    return { visible, hidden };
  }
  const displaced = visible[visibleCount - 1];
  return {
    visible: [...visible.slice(0, visibleCount - 1), hidden[activeIndex]],
    hidden: hidden.map((f, i) => (i === activeIndex ? displaced : f)),
  };
};

const measureRow = (row: HTMLElement, filterCount: number, hasToggle: boolean): RowMeasurements => {
  const children = Array.from(row.children).filter(
    (c): c is HTMLElement => c instanceof HTMLElement,
  );
  const first = hasToggle ? 1 : 0;
  const chips = children.slice(first, first + filterCount);
  const overflowButton = children[first + filterCount];
  const rowLeft = row.getBoundingClientRect().left;
  const chipRects = chips.map((chip) => chip.getBoundingClientRect());
  const gap = chipRects.length > 1 ? Math.max(0, chipRects[1].left - chipRects[0].right) : 0;
  return {
    available: row.clientWidth,
    extents: chipRects.map((rect) => rect.right - rowLeft),
    reserved: (overflowButton?.getBoundingClientRect().width ?? 0) + gap,
  };
};

const useFittingFilterCount = (
  filters: QuickFilter[],
  isExpanded: boolean,
  hasToggle: boolean,
  wrap: boolean,
) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(0);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const filterKey = filters.map((f) => f.id).join("|");

  useEffect(() => {
    setVisibleCount(null);
  }, [filterKey, isExpanded]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) {
      return;
    }
    const observer = new ResizeObserver(
      debounce((entries: ResizeObserverEntry[]) => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (Math.abs(width - widthRef.current) > 1) {
          widthRef.current = width;
          setVisibleCount(null);
        }
      }, 100),
    );
    observer.observe(row);
    return () => {
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || !isExpanded || wrap || visibleCount !== null) {
      return;
    }
    setVisibleCount(countFittingFilters(measureRow(row, filters.length, hasToggle)));
  }, [visibleCount, isExpanded, filters.length, hasToggle, wrap]);

  return { rowRef, visibleCount: wrap ? filters.length : visibleCount };
};

export const QuickFilterBar = observer(
  ({
    filters,
    isExpanded = true,
    onToggle,
    collapseFiltersLabel,
    expandFiltersLabel,
    tooltipMessage,
    wrap = false,
    extraAction,
    ...boxProps
  }: QuickFilterBarProps) => {
    const hasToggle = onToggle !== undefined;
    const showFilters = isExpanded || !hasToggle;
    const { rowRef, visibleCount } = useFittingFilterCount(filters, showFilters, hasToggle, wrap);
    if (filters.length === 0) {
      return null;
    }

    const { visible, hidden } = splitQuickFilters(filters, visibleCount ?? filters.length);
    return (
      <Box
        ref={rowRef}
        alignItems={"center"}
        gap={"sm"}
        wrap={wrap}
        overflow={wrap ? undefined : "hidden"}
        whitespace={"nowrap"}
        {...boxProps}
      >
        {showFilters ? (
          <>
            {hasToggle && (
              <ToggleButton
                variant={"small"}
                icon={{ name: "caret-left", size: "sm" }}
                onClick={onToggle}
                tooltip={expandFiltersLabel ?? { key: "quick-filters.hide" }}
              />
            )}
            {visible.map(({ id, label, isActive, onSelect, tooltip }) => (
              <ToggleButton
                key={id}
                variant={"small"}
                label={label}
                selected={isActive}
                onClick={onSelect}
                tooltip={tooltip}
              />
            ))}
            {hidden.length > 0 ? (
              <DropdownMenu
                button={({ props }) => (
                  <ToggleButton
                    variant={"small"}
                    icon={{ name: "dotdotdot", size: "sm" }}
                    tooltip={tooltipMessage ?? { key: "quick-filters.more" }}
                    {...props}
                  />
                )}
                items={[
                  ...hidden.map<DropdownMenuItemType>(({ label, icon, onSelect, tooltip }) => ({
                    type: "item",
                    label,
                    icon,
                    onSelect,
                    tooltip,
                  })),
                  extraAction && { type: "separator" },
                  extraAction && {
                    type: "item",
                    label: extraAction.label,
                    icon: extraAction.icon,
                    onSelect: extraAction.onSelect,
                  },
                ]}
              />
            ) : (
              extraAction && (
                <ToggleButton
                  variant={"small"}
                  icon={extraAction.icon}
                  onClick={extraAction.onSelect}
                  tooltip={extraAction.tooltip}
                />
              )
            )}
          </>
        ) : (
          <ToggleButton
            variant={"small"}
            label={collapseFiltersLabel ?? { key: "quick-filters.show" }}
            icon={"caret-right"}
            iconPlacement={"end"}
            onClick={onToggle}
          />
        )}
      </Box>
    );
  },
);
